import { useEffect, useRef, ReactNode } from "react"

import { InputAdornment, TextFieldProps } from "@mui/material"

import { VisibilityIcon } from "@l2r-front/l2r-icons"

import type { TypographyVariant } from "../../types"

import * as Styled from "./L2RTextField.styled"

type L2RTextFieldProps = TextFieldProps & {
    labelVariant?: TypographyVariant,
    iconEnd?: ReactNode,
    iconStart?: ReactNode,
    selectInputEnd?: boolean,
}

export const L2RTextField = (props: L2RTextFieldProps) => {

    const {
        className,
        children,
        label,
        labelVariant,
        iconStart,
        iconEnd,
        selectInputEnd,
        ...textFieldProps
    } = props

    const ref = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (ref.current && selectInputEnd) {
            const firstChild = ref.current.firstChild
            if (!(firstChild instanceof HTMLElement)) return

            const inputEl = firstChild.firstChild
            if (!(inputEl instanceof HTMLInputElement)) return

            inputEl.setSelectionRange(inputEl.value.length, inputEl.value.length)
        }
    }, [selectInputEnd])

    const currentText = (textFieldProps.slotProps?.htmlInput as { value?: string })?.value ?? "";
    const hasChips = Boolean((textFieldProps.slotProps?.input as { startAdornment?: React.ReactElement<{ children?: React.ReactNode }> })?.startAdornment?.props.children);
    const isEmpty = !hasChips && currentText === "";

    const placeholder = isEmpty ? (textFieldProps.placeholder || label?.toString()) : undefined;

    return (
        <Styled.TextField
            className={className}
            ref={ref}
            label={label}
            placeholder={placeholder}
            slotProps={{
                formHelperText: {
                    id: textFieldProps.error && `${textFieldProps.id}-error`,
                },
                input: {
                    ...(iconStart && {
                        startAdornment: (
                            <InputAdornment position="start">{iconStart}</InputAdornment>
                        ),
                    }),
                    ...(iconEnd && {
                        endAdornment: (
                            <InputAdornment position="end">{iconEnd}</InputAdornment>
                        ),
                    }),
                },
            }}
            sx={{
                display: textFieldProps.type === "hidden" ? "none" : "block",
            }}
            {...textFieldProps}>
            {children}
            {textFieldProps.type === "password" && <VisibilityIcon />}
        </Styled.TextField>
    )
}
